import { Link } from "react-router";
import { format } from "date-fns";
import { Badge } from "../../app/components/ui/badge";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Progress } from "../../app/components/ui/progress";
import { useBusinessProfile } from "./hooks/useBusinessProfile";

export function BusinessProfileViewPage() {
  const { profile, error, loading } = useBusinessProfile();

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Loading profile…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">No profile data.</p>
      </div>
    );
  }

  const lastAt = profile.last_updated_at ? new Date(profile.last_updated_at) : null;

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">Business profile</h1>
          <p className="text-muted-foreground">Your store details and onboarding progress.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {profile.updated_after_verification ? (
            <Badge variant="secondary" className="bg-amber-100 text-amber-900 border-amber-200">
              Profile updated
            </Badge>
          ) : null}
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/business-profile/edit">Edit profile</Link>
          </Button>
        </div>
      </div>

      {lastAt ? (
        <p className="text-sm text-muted-foreground">
          Last updated: {format(lastAt, "PPpp")}
        </p>
      ) : null}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Completion</CardTitle>
          <CardDescription>Steps completed for your business profile.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Progress</span>
            <span>{profile.completion_percentage}%</span>
          </div>
          <Progress value={profile.completion_percentage} className="h-2" />
          <p className="text-sm text-muted-foreground">
            Store type: <span className="font-medium text-foreground">{profile.store_type ?? "—"}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Completed steps:{" "}
            <span className="font-medium text-foreground">{profile.completed_steps.join(", ") || "—"}</span>
          </p>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Profile data</CardTitle>
          <CardDescription>Raw profile payload (JSON).</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-muted/50 rounded-lg p-4 overflow-auto max-h-96">
            {JSON.stringify(profile.profile_data ?? {}, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
